import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CheckCircle, Copy, Code2, Settings } from 'lucide-react';
import { fieldTypeLabels } from './templateConstants';

export default function MergeFieldsHelpDialog({ 
  open, 
  onOpenChange, 
  mergeFieldsData,
  customFields 
}) {
  const [copiedField, setCopiedField] = useState(null);

  const copyMergeField = (tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedField(tag);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const defaultFields = mergeFieldsData?.default_fields || [];
  const customMergeFields = mergeFieldsData?.custom_fields || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="w-5 h-5" />
            Available Merge Fields
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="default" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="default">System Fields ({defaultFields.length})</TabsTrigger>
            <TabsTrigger value="custom">Custom Fields ({customMergeFields.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="default" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-2">
              {defaultFields.map((field) => (
                <FieldRow 
                  key={field.tag} 
                  field={field} 
                  copiedField={copiedField}
                  onCopy={copyMergeField}
                />
              ))}
            </div>
          </TabsContent>
          <TabsContent value="custom" className="flex-1 overflow-y-auto mt-4">
            {customMergeFields.length > 0 ? (
              <div className="space-y-2">
                {customMergeFields.map((field) => (
                  <FieldRow 
                    key={field.tag} 
                    field={field} 
                    copiedField={copiedField}
                    onCopy={copyMergeField}
                    isCustom
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No custom fields created yet</p>
                <p className="text-xs mt-1">Create custom fields in the "Custom Fields" tab</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ field, copiedField, onCopy, isCustom }) {
  const isCopied = copiedField === field.tag;
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-slate-200 px-2 py-0.5 rounded">
            {field.tag}
          </code>
          {isCustom && field.field_type && (
            <Badge variant="outline" className="text-xs">
              {fieldTypeLabels[field.field_type] || field.field_type}
            </Badge>
          )}
          {field.required && (
            <Badge variant="secondary" className="text-xs">Required</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{field.description || field.name}</p>
        {field.example && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Example: <span className="font-mono">{field.example}</span>
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onCopy(field.tag)}
        className={isCopied ? 'text-green-600' : ''}
      >
        {isCopied ? (
          <>
            <CheckCircle className="w-4 h-4 mr-1" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </>
        )}
      </Button>
    </div>
  );
}
